import React, { useState, useEffect, memo } from 'react';
import { Button, Card, Container, Form, Modal, Spinner } from 'react-bootstrap';
import { BoxArrowUp, Clipboard, ClipboardCheck, FileText, InfoCircle, Search } from 'react-bootstrap-icons';
import { useNavigate } from "react-router-dom";
import { Browser } from '@capacitor/browser';
import { useTranslation } from 'react-i18next';
import useCollections, { CollectionModal, CollectionIcon } from '../../hooks/useCollections';

const openCapacitorSite = async (link) => {
  await Browser.open({ url: link });
};

function formatDate(inputDate) {
  if (!inputDate) return '';

  // If it's already in M-D-YYYY format, return as-is
  if (typeof inputDate === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(inputDate)) {
    return inputDate;
  }

  try {
    const date = new Date(inputDate);
    if (isNaN(date.getTime())) return '';

    return `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}`;
  } catch (error) {
    return '';
  }
}

const APIMemberVotePullModal = memo(({ bio_guide_id }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [voteInfo, setVoteInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const apiKey = process.env.REACT_APP_API_KEY;

  // Share modal states
  const [shareModalIsOpen, setShareModalIsOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  // Use the collection hook
  const {
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    openCollectionModal,
    closeModal,
    handleSave,
    isStorageAvailable
  } = useCollections();

  var schema = 'united_states_of_america';
  var table = 'all_member_votes';

  const handleDetailsClick = (id) => {
    navigate(`/feed/${schema}/${table}/${id}`);
  };

  // Built separately from the click handler so the folder icon can check the
  // same object against the saved collection.
  // feedName/branch/topic stay English per stored-data convention
  const buildCollectionItem = (vote) => {
    if (!vote) return null;

    return {
      title: vote.title,
      url: vote.url || `https://news.govglance.org/feed/${schema}/${table}/${vote.roll_call_number}`,
      docLink: vote.document_link || '',
      date: vote.created_at, // Raw date
      feedName: 'Votes',
      branch: 'Legislative',
      topic: 'Member Votes'
    };
  };

  // Handle adding to collection
  const handleAddToCollection = (vote) => {
    const collectionItem = buildCollectionItem(vote);
    if (!collectionItem) return;
    openCollectionModal(collectionItem);
  };

  useEffect(() => {
    const fetchVoteInfo = async () => {
      setLoading(true);
      try {
        const response = await fetch(`https://api.govglance.org/posts/recent?limit=10000&skip=0&schema=united_states_of_america&table=all_member_votes&order_by=created_at&filter_column=bio_guide_id&filter_string=${bio_guide_id}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', "Authorization": `Bearer ${apiKey}` },
        });
        const data = await response.json();
        setVoteInfo(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching vote info:', error);
        setVoteInfo([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVoteInfo();
  }, [bio_guide_id]);

  const handleShareClick = (id) => {
    const shareUrl = `news.govglance.org/feed/united_states_of_america/all_member_votes/${id}`;
    setShareLink(shareUrl);
    setShareModalIsOpen(true);
    setLinkCopied(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    });
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: '',
          text: '',
          url: shareLink,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      alert(t('common:shareUnsupported', 'Sharing is not supported on this device.'));
    }
  };

  const handleSearchInputChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // The whole vote list is already in memory (the fetch pulls everything for
  // this member), so search filters locally instead of refetching per keystroke.
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredVotes = normalizedQuery
    ? voteInfo.filter((vote) => (vote.title || '').toLowerCase().includes(normalizedQuery))
    : voteInfo;

  const renderVotes = () => {
    if (loading) {
      // Flex wrapper rather than text-center: Spinner's display can be
      // overridden, and justify-content holds regardless of what it resolves to.
      return (
        <div className="vote-spinner-wrap">
          <Spinner id='spinner' animation="grow" variant="light" />
        </div>
      );
    }

    if (filteredVotes.length === 0) {
      return <p className="text-center">{t('common:noDataAvailable', 'No data available.')}</p>;
    }

    return filteredVotes.map((vote) => (
      <div id='fullBillFeed' key={vote.id}>
        <Container id='feedContainerNormalView'>
          <span>{vote.title.length > 180 ? `${vote.title.slice(0, 177)}...` : vote.title}</span>
          <Card.Body
            id='billBodyText'
            onClick={() => openCapacitorSite(vote.document_link, "_blank")}
            aria-label="Open Document Link"
          ></Card.Body>
          <p>
            <Card.Body id="voteBodyText">
              <p>{t('common:dateLabel', 'Date: {{date}}', { date: formatDate(vote.created_at) })}</p>
              <p>{t('common:statusLabel', 'Status: {{status}}', { status: vote.status })}</p>
              <p>{t('common:voteLabel', 'Vote: {{vote}}', { vote: vote.vote })}</p>
            </Card.Body>

            <Button id='billLink' onClick={() => handleDetailsClick(vote.roll_call_number)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg> {t('common:more', 'More')}
            </Button>
            <Button id='viewBillButton' onClick={() => openCapacitorSite(vote.document_link, "_blank")}>
              <FileText /> {t('common:document', 'Document')}
            </Button>
            <Button id='addToCollectionButton' onClick={() => handleAddToCollection(vote)}>
              <CollectionIcon item={buildCollectionItem(vote)} />
            </Button>
            <Button
              id="shareButton"
              onClick={(e) => {
                e.stopPropagation();
                handleShareClick(vote.roll_call_number);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-send" viewBox="0 0 16 16">
                <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z" />
              </svg>
            </Button>
          </p>
        </Container>
        <br></br>
      </div>
    ));
  };

  return (
    <>
      <div id='searchForm'>
        <Form className="text-center" onSubmit={(e) => e.preventDefault()}>
          <Form.Group className="d-flex justify-content-center align-items-center">
            <Form.Control
              variant="light"
              id="searchInput"
              placeholder={t('common:searchPlaceholder', 'Search...')}
              type="text"
              color="white"
              value={searchQuery}
              onChange={handleSearchInputChange}
            />
            <Button id='ggButton' type="button">
              <Search />
            </Button>
          </Form.Group>
        </Form>
      </div>

      <div id='fullBillFeed'>
        {renderVotes()}
      </div>

      {/* Share Modal */}
      <Modal
        id="shareModal"
        show={shareModalIsOpen}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        onHide={() => setShareModalIsOpen(false)}
      >
        <Modal.Header id="billModalHeader" closeButton>
          <h3>{t('common:share', 'Share')}</h3>
        </Modal.Header>
        <Modal.Body id="descriptionModalBody">
          <Form.Group id="noteModalForm">
            <Form.Control
              id="noteModalFeedItem"
              type="text"
              readOnly
              value={shareLink}
            />
          </Form.Group>
          <Modal.Footer id="shareFooter">
            <Button id="addToCollectionButton" onClick={() => handleNativeShare()}>
              <BoxArrowUp />
            </Button>
            <Button id="addToCollectionButton" onClick={handleCopyLink}>
              {linkCopied ? <ClipboardCheck /> : <Clipboard />}
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>

      {/* Collection Modal */}
      <CollectionModal
        isModalOpen={isModalOpen}
        currentItem={currentItem}
        saveSuccess={saveSuccess}
        note={note}
        setNote={setNote}
        handleSave={handleSave}
        closeModal={closeModal}
        isStorageAvailable={isStorageAvailable}
      />

      <style>{`
        .vote-spinner-wrap {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 2rem 0;
        }
      `}</style>
    </>
  );
});

export default APIMemberVotePullModal;